import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  /* Brand fonts (served from /public/fonts, respecting PUBLIC_URL for /pepe deployments) */
  @font-face {
    font-family: 'Gilroy Heavy';
    src:
      url('${process.env.PUBLIC_URL}/fonts/gilroy/Gilroy-Heavy.ttf') format('truetype');
    font-weight: 800;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Adilezy';
    src:
      url('${process.env.PUBLIC_URL}/fonts/adilezy-display-typeface-2023-12-14-18-52-43-utc/Adilezy.woff') format('woff'),
      url('${process.env.PUBLIC_URL}/fonts/adilezy-display-typeface-2023-12-14-18-52-43-utc/Adilezy.ttf') format('truetype'),
      url('${process.env.PUBLIC_URL}/fonts/adilezy-display-typeface-2023-12-14-18-52-43-utc/Adilezy.otf') format('opentype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  
  :root {
    /* Brand palette */
    --brand-blue: #014a90;
    --brand-blue-dark: #013a70;
    --brand-cream: #e3e3dd;
    --brand-gold: #d38d54;
    --brand-teal: #3aaeab;

    /* Legacy vars (used across components) mapped to brand palette */
    --primary-turquoise: var(--brand-teal);
    --light-turquoise: var(--brand-teal);
    --dark-turquoise: #2f9b98;
    --navy-blue: var(--brand-blue);
    --dark-navy: var(--brand-blue-dark);
    --white: var(--brand-cream);
    --light-gray: #efefe8;
    --text-dark: var(--brand-blue);

    --gradient-primary: linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-teal) 100%);
    --gradient-secondary: linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-blue-dark) 100%);
    --gradient-gold: linear-gradient(135deg, rgba(211, 141, 84, 0.95) 0%, rgba(211, 141, 84, 0.65) 100%);

    --shadow-light: 0 4px 20px rgba(58, 174, 171, 0.15);
    --shadow-medium: 0 8px 30px rgba(1, 74, 144, 0.2);
    --shadow-heavy: 0 15px 50px rgba(1, 74, 144, 0.3);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
    overflow-x: hidden;
  }

  body {
    font-family: 'Gilroy Heavy', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
    font-weight: 800;
    line-height: 1.6;
    color: var(--text-dark);
    background: var(--white);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Darker Grotesque', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
    font-weight: 700;
    color: inherit;
  }

  /* Accent underline (for words you want to highlight) */
  u,
  .accent-underline {
    text-decoration-color: var(--brand-teal);
    text-decoration-thickness: 0.18em;
    text-underline-offset: 0.18em;
  }

  ::selection {
    background: rgba(58, 174, 171, 0.35);
    color: var(--brand-blue);
  }

  a {
    text-decoration: none;
    color: inherit;
    transition: all 0.3s ease;
  }

  a:hover {
    text-decoration: underline;
    text-decoration-color: var(--brand-teal);
    text-decoration-thickness: 0.15em;
    text-underline-offset: 0.18em;
  }

  button {
    border: none;
    outline: none;
    cursor: pointer;
    font-family: inherit;
    transition: all 0.3s ease;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--navy-blue);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--primary-turquoise);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--dark-turquoise);
  }

  /* Utility Classes */
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .section-padding {
    padding: 100px 0;
  }

  .gradient-text {
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .glass-effect {
    background: rgba(227, 227, 221, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(227, 227, 221, 0.2);
    border-radius: 15px;
  }

  /* Animation Classes */
  .fade-in {
    opacity: 0;
    transform: translateY(30px);
  }

  .slide-in-left {
    opacity: 0;
    transform: translateX(-50px);
  }

  .slide-in-right {
    opacity: 0;
    transform: translateX(50px);
  }

  .scale-in {
    opacity: 0;
    transform: scale(0.8);
  }

  .rotate-in {
    opacity: 0;
    transform: rotate(-10deg) scale(0.9);
  }

  /* Media Queries */
  @media (max-width: 1024px) {
    .container {
      padding: 0 15px;
    }
    
    .section-padding {
      padding: 80px 0;
    }
  }

  @media (max-width: 768px) {
    .section-padding {
      padding: 60px 0;
    }
    
    body {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    .container {
      padding: 0 10px;
    }
    
    .section-padding {
      padding: 40px 0;
    }
  }
`;

export default GlobalStyles;